import { ApplicationError } from "../../models/errors/application-error";
import { ApplicationProductList } from "../../models/product/application-product-list";
import { ApplicationProduct } from "../../models/product/application-product";
import client from "../../external/open-api";
import IAdminProductService from "./admin-product-service-interface";
import { components } from "../../external/schema/order-link-api-schema";
import { ApplicationProductDetail } from "../../models/product/application-product-detail";
import { mapStringToEnum } from "../../helpers/enum-helper";
import { ApplicationSizeUnit } from "../../models/product/application-size-unit";
import { ApplicationBranch } from "../../models/organisation/application-branch";
import BaseService from "../base-service";
import { ApplicationVAT } from "../../models/finance/application-vat";
import { ApplicationDepartment } from "../../models/organisation/application-department";
import { ApplicationOrderTemplate } from "../../models/organisation/application-order-template";
import { ApplicationOrderingMode } from "../../models/organisation/application-ordering-mode";
import { ApplicationSubDepartment } from "../../models/organisation/application-sub-department";
import { ApplicationProductSelectionMode } from "../../models/organisation/application-product-selection-mode";
import { ApplicationProductUpload } from "../../models/product/application-product-upload";
export default class AdminProductService extends BaseService implements IAdminProductService {


  async getProductsBySubDepartment(
    subDepartmentId: number,
    currentPosition: number,
    fetchNext: number,
    searchText: string
  ): Promise<ApplicationProductList> {
    const { data, error } = await this.withRetry(() => client.GET(
      `/v{version}/api/manageproduct/subdepartment/{subDepartmentId}`,
      {
        params: {
          path: { version: "1", subDepartmentId },
          query: {
            "Paging.CurrentPosition": currentPosition,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": false,
            SearchText: searchText,
          },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch products");
    }
    return {
      products: this.mapToApplicationProduct(data.data),
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }
  async getProductById(productId: number): Promise<ApplicationProductDetail> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/manageproduct/{productId}",
      {
        params: {
          path: { version: "1", productId },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to get product");
    }

    return this.mapToProductDetail(data.data);
  }

  async updateOrder(productId: number, order: number): Promise<ApplicationProduct> {
    const { data, error } = await this.withRetry(() => client.PATCH(
      "/v{version}/api/manageproduct/{productId}",
      {
        params: {
          path: { version: "1", productId },
        },
        body: {
          order
        }
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to get product");
    }

    return data.data as ApplicationProduct;
  }

  async deleteProduct(productId: number): Promise<void> {
    const { data, error } = await this.withRetry(() => client.DELETE(
      "/v{version}/api/manageproduct/{productId}",
      {
        params: {
          path: { version: "1", productId },
        },
      }
    ));

    if (!data?.success || error) {
      throw new ApplicationError("Failed to delete product");
    }
  }

  async insertManageProduct(
    product: ApplicationProductDetail
  ): Promise<ApplicationProduct> {
    const productDto = this.mapToProductDTO(product);
    const { data, error } = await this.withRetry(() => client.POST("/v{version}/api/manageproduct", {
      params: {
        path: { version: "1" },
      },
      body: {
        ...productDto,
      },
    }));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to update Product");
    }

    return data.data as ApplicationProduct;
  }

  async updateManageProduct(
    product: ApplicationProductDetail
  ): Promise<ApplicationProduct> {
    const productDto = this.mapToProductDTO(product);
    const { data, error } = await this.withRetry(() => client.PUT("/v{version}/api/manageproduct", {
      params: {
        path: { version: "1" },
      },
      body: {
        ...productDto,
      },
    }));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to update Product");
    }

    return data.data as ApplicationProduct;
  }


  async bulkUploadProducts(
    departmentId: number,
    subDepartmentId: number,
    products: ApplicationProductUpload[],
    fetchNext: number,
  ): Promise<ApplicationProductList | null> {

    const { data, error } = await this.withRetry(() => client.POST(
      "/v{version}/api/manageproduct/bulk",
      {
        params: {
          path: { version: "1" },
          query: {
            "Paging.CurrentPosition": 0,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": false,
          },
        },
        body: {
          departmentId: departmentId,
          subDepartmentId: subDepartmentId,
          products: products,
        },
      }
    ));

    if (error) {
      throw new ApplicationError("Failed to bulk upload products");
    }
    if (!data?.data || !data?.success) {
      throw new ApplicationError("Unexpected response format");
    }

    return {
      products: this.mapToApplicationProduct(data.data),
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  private mapToApplicationProduct(
    data: components["schemas"]["GetProductsResponse"] | undefined
  ): ApplicationProduct[] {
    if (!data) {
      return [];
    }

    return (
      data.products?.map((d) => ({
        id: d.id ?? 0,
        department: d.department == null ? undefined : this.MapToDepartment(d.department),
        subDepartment: d.subDepartment == null ? undefined : this.MapToSubDepartment(d.subDepartment),
        name: d.name ?? "",
        image: d.image ?? "",
        plu: d.plu ?? "",
        active: d.active ?? false,
        order: d.order ?? 0,
        canDelete: d.canDelete ?? false,
        vat: this.mapToVat(d.vat!)
      })) || []
    );
  }

  private mapToVat(vat: components["schemas"]["VatDTO"]): ApplicationVAT {
    return {
      id: vat.id ?? 0,
      description: vat.description ?? "",
      rate: vat.rate ?? 0
    }
  }
  private MapToDepartment(dept: components["schemas"]["DepartmentDTO"]): ApplicationDepartment {
    return {
      id: dept.id ?? 0,
      name: dept.name ?? "",
      active: dept.active ?? false,
      canDelete: dept.canDelete ?? false,
      orderTemplate: mapStringToEnum(ApplicationOrderTemplate, dept.orderTemplate ?? ""),
      orderingMode: mapStringToEnum(ApplicationOrderingMode, dept.orderingMode ?? "")
    }
  }

  private MapToSubDepartment(dept: components["schemas"]["SubDepartmentDTO"]): ApplicationSubDepartment {
    return {
      id: dept.id ?? 0,
      departmentId: dept.departmentId ?? 0,
      description: dept.description ?? "",
      name: dept.name ?? "",
      active: dept.active ?? false,
      canDelete: dept.canDelete ?? false,
      productSelectionMode: mapStringToEnum(ApplicationProductSelectionMode, dept.productSelectionMode ?? "")
    }
  }

  private mapToProductDetail(
    data: components["schemas"]["ProductDetailDTO"]
  ): ApplicationProductDetail {
    return {
      id: data.id ?? 0,
      name: data.name ?? "",
      departmentId: data.departmentId,
      subDepartmentId: data.subDepartmentId,
      description: data.description ?? "",
      image: data.image ?? "",
      plu: data.plu ?? "",
      active: data.active ?? false,
      information: data.productInformation ?? {},
      suggestedQuantity: data.suggestedQuantity ?? 0,
      supplierSizeUnit: data.supplierSizeUnit
        ? mapStringToEnum(ApplicationSizeUnit, data.supplierSizeUnit)
        : undefined,
      branches:
        data.branches?.map((branch: components["schemas"]["BranchDTO"]) => ({
          id: branch.id ?? 0,
          name: branch.name ?? "",
        })) ?? [],
      vatId: data.vatId ?? 0
    };
  }

  private mapToProductDTO(
    product: ApplicationProductDetail
  ): components["schemas"]["ProductDetailDTO"] {
    return {
      id: product.id,
      name: product.name,
      description: product.description,
      departmentId: product.departmentId,
      subDepartmentId: product.subDepartmentId,
      image: product.image,
      plu: product.plu,
      active: product.active,
      productInformation: product.information,
      suggestedQuantity: product.suggestedQuantity,
      supplierSizeUnit: product.supplierSizeUnit,
      branches: product.branches.map((branch: ApplicationBranch) => ({
        id: branch.id,
        name: branch.name,
      })),
      vatId: product.vatId
    };
  }
}
